import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { getYoxlamaStats, YoxlamaStats, getAllAssignments } from '../../services/adminService';
import Icon from '../icons/Icon';
import { getSelectedExam } from '../../services/examService';
import { YoklaAssignment } from '../../types';
import { SINIFLAR, QRUPLAR, QRUP_CONFIGS } from '../../constants/fennConfig';

const Container = styled.div`
  min-height: 100vh;
  background: #1e3a8a;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;

const TabContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const TabButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const TabButton = styled.button<{ active?: boolean }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.active ? '#3b82f6' : '#e2e8f0'};
  background: ${props => props.active ? '#3b82f6' : 'white'};
  color: ${props => props.active ? 'white' : '#64748b'};
  
  &:hover {
    border-color: #3b82f6;
    ${props => !props.active && 'background: #f8fafc;'}
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
    flex: 1;
    min-width: 120px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StatCard = styled.div<{ color?: string }>`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${props => props.color || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'};
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const StatDescription = styled.div`
  font-size: 13px;
  color: #94a3b8;
`;

const ClassSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ClassTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const QrupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const QrupCard = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 20px;
  border: 2px solid #e2e8f0;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const QrupTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #475569;
  margin: 0 0 16px 0;
`;

const QrupStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const QrupStatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: white;
  border-radius: 8px;
  font-size: 14px;
`;

const QrupStatLabel = styled.span`
  color: #64748b;
  font-weight: 500;
`;

const QrupStatValue = styled.span<{ color?: string }>`
  color: ${props => props.color || '#1e293b'};
  font-weight: 700;
  font-size: 16px;
`;

const ProgressBar = styled.div<{ percentage: number }>`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 8px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.percentage}%;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    transition: width 0.3s ease;
  }
`;

const FennList = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const FennItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 13px;
`;

const FennName = styled.span`
  color: #475569;
  font-weight: 600;
`;

const FennStats = styled.span`
  color: #64748b;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AssignmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AssignmentCard = styled.div<{ status?: string }>`
  background: white;
  border-radius: 12px;
  padding: 16px;
  border-left: 4px solid ${props => {
    switch (props.status) {
      case 'completed': return '#10b981';
      case 'passive': return '#ef4444';
      default: return '#3b82f6';
    }
  }};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const AssignmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const AssignmentTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const AssignmentStatusBadge = styled.div<{ status?: string }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'completed': return 'linear-gradient(135deg, #10b981, #059669)';
      case 'passive': return 'linear-gradient(135deg, #ef4444, #dc2626)';
      default: return 'linear-gradient(135deg, #3b82f6, #2563eb)';
    }
  }};
  color: white;
`;

const AssignmentDetails = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
`;

const StatisticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<YoxlamaStats | null>(null);
  const [assignments, setAssignments] = useState<YoklaAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'detailed' | 'assignments'>('overview');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const selectedExam = getSelectedExam();
      const collectionFilter = selectedExam?.collection || 'scanned2610';
      
      console.log('📊 StatisticsPage: Seçili imtahan:', selectedExam);
      console.log('🔍 StatisticsPage: Collection filter:', collectionFilter);
      
      const [statsData, assignmentsData] = await Promise.all([
        getYoxlamaStats(collectionFilter),
        getAllAssignments(collectionFilter)
      ]);
      
      console.log('✅ StatisticsPage: Yüklenen atamalar:', assignmentsData.length);
      
      setStats(statsData);
      setAssignments(assignmentsData);
    } catch (error) {
      console.error('Məlumat yüklənərkən xəta:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinner />
          <div>Statistikalar yüklənir...</div>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title><Icon name="stats" size={20} style={{ marginRight: 8 }} /> Ətraflı Statistikalar</Title>
        <Button onClick={() => navigate('/admin/dashboard')}>
          ← Geri Qayıt
        </Button>
      </Header>

      <TabContainer>
          <TabButtons>
            <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
              Ümumi Baxış
            </TabButton>
            <TabButton active={activeTab === 'detailed'} onClick={() => setActiveTab('detailed')}>
              Ətraflı Statistikalar
            </TabButton>
            <TabButton active={activeTab === 'assignments'} onClick={() => setActiveTab('assignments')}>
              Tapşırıqlar
            </TabButton>
            <TabButton onClick={() => navigate('/admin/students')}>
              Şagird Nəticələri
            </TabButton>
          </TabButtons>

        {activeTab === 'overview' && stats && (
          <>
            <StatsGrid>
              <StatCard color="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)">
                <StatLabel>Ümumi Yoxlama Məlumatı</StatLabel>
                <StatValue>{stats.totalScannedData}</StatValue>
                <StatDescription>Sistemdeki tüm yoklama verileri</StatDescription>
              </StatCard>

              <StatCard color="linear-gradient(135deg, #10b981 0%, #059669 100%)">
                <StatLabel>Tayin Edilmiş Atamalar</StatLabel>
                <StatValue>{stats.totalAssignments}</StatValue>
                <StatDescription>
                  {stats.activeAssignments} aktif • {stats.completedAssignments} tamamlandı • {stats.passiveAssignments} pasif
                </StatDescription>
              </StatCard>

              <StatCard color="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
                <StatLabel>Tamamlanan Yoxlamalar</StatLabel>
                <StatValue>{stats.totalCompleted}</StatValue>
                <StatDescription>
                  {stats.totalRemaining} yoklama kaldı
                </StatDescription>
                <ProgressBar 
                  percentage={(stats.totalCompleted / (stats.totalCompleted + stats.totalRemaining)) * 100} 
                />
              </StatCard>

              <StatCard color="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)">
                <StatLabel>Aktif Öğretmenler</StatLabel>
                <StatValue>{stats.activeTeachers}</StatValue>
                <StatDescription>Toplam {stats.totalTeachers} öğretmen</StatDescription>
              </StatCard>
            </StatsGrid>
          </>
        )}

        {activeTab === 'detailed' && stats && (
          <>
            {SINIFLAR.map(sinif => {
              const classData = stats.byClass[sinif];
              if (!classData) return null;

              return (
                <ClassSection key={sinif}>
                  <ClassTitle>
                    📚 {sinif}. Sınıf
                    <span style={{ fontSize: '16px', color: '#64748b', fontWeight: 'normal' }}>
                      ({classData.completed}/{classData.total} tamamlandı)
                    </span>
                  </ClassTitle>

                  <QrupGrid>
                    {QRUPLAR.map(qrup => {
                      const qrupData = classData.byQrup[qrup];
                      if (!qrupData) return null;

                      const qrupConfig = QRUP_CONFIGS.find(q => q.qrup === qrup);
                      const percentage = qrupData.total > 0 
                        ? (qrupData.completed / qrupData.total) * 100 
                        : 0;

                      return (
                        <QrupCard key={qrup}>
                          <QrupTitle>Qrup {qrup}</QrupTitle>
                          <QrupStats>
                            <QrupStatRow>
                              <QrupStatLabel>Toplam:</QrupStatLabel>
                              <QrupStatValue>{qrupData.total}</QrupStatValue>
                            </QrupStatRow>
                            <QrupStatRow>
                              <QrupStatLabel>Tamamlandı:</QrupStatLabel>
                              <QrupStatValue color="#10b981">{qrupData.completed}</QrupStatValue>
                            </QrupStatRow>
                            <QrupStatRow>
                              <QrupStatLabel>Kaldı:</QrupStatLabel>
                              <QrupStatValue color="#ef4444">{qrupData.remaining}</QrupStatValue>
                            </QrupStatRow>
                          </QrupStats>
                          <ProgressBar percentage={percentage} />

                          {qrupData.byFenn && Object.keys(qrupData.byFenn).length > 0 && (
                            <FennList>
                              {Object.entries(qrupData.byFenn).map(([fenn, fennData]) => (
                                <FennItem key={fenn}>
                                  <FennName>{fenn}</FennName>
                                  <FennStats>
                                    {fennData.completed}/{fennData.total}
                                  </FennStats>
                                </FennItem>
                              ))}
                            </FennList>
                          )}
                        </QrupCard>
                      );
                    })}
                  </QrupGrid>
                </ClassSection>
              );
            })}
          </>
        )}

        {activeTab === 'assignments' && (
          <AssignmentsList>
            {assignments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                Henüz atama yok
              </div>
            ) : (
              assignments.map(assignment => (
                <AssignmentCard key={assignment.id} status={assignment.status}>
                  <AssignmentHeader>
                    <AssignmentTitle>
                      {assignment.hocaName} - {assignment.fenn}
                    </AssignmentTitle>
                    <AssignmentStatusBadge status={assignment.status}>
                      {assignment.status === 'completed' ? 'Tamamlandı' : 
                       assignment.status === 'passive' ? 'Pasif' : 'Aktif'}
                    </AssignmentStatusBadge>
                  </AssignmentHeader>
                  <AssignmentDetails>
                    Sınıf: {assignment.sinif} • Qrup: {assignment.qrup}
                    {assignment.subGroup && ` • SubGroup: ${assignment.subGroup}`}
                    <br />
                    İləriləmə: {assignment.completedCount || 0}/{assignment.totalCount || 0}
                  </AssignmentDetails>
                </AssignmentCard>
              ))
            )}
          </AssignmentsList>
        )}
      </TabContainer>
    </Container>
  );
};

export default StatisticsPage;


